/**
 * TwoColumnRenderer — two-column
 * Two equal-width content columns each with title + optional body + optional bullets.
 */
import React from "react";
import { SurfaceCard } from "../../ui/SurfaceCard";
import { Title } from "../../ui/Title";
import { Text } from "../../ui/Text";
import { Eyebrow } from "../../ui/Eyebrow";
import { BulletListUI } from "../../ui/BulletListUI";
import { EditableText } from "../EditableText";
import { AnimatedField } from "../../animations/AnimatedField";
import { resolveText, resolveTextOptional, resolveTextArray } from "../../utils/resolveText";

/* eslint-disable react/prop-types */

function fieldAnims(field) {
  if (!field || typeof field !== "object") return [null, null];
  return [field.boxAnimation ?? null, field.boxExitAnimation ?? null];
}

function Column({ col, colIndex, editing, onChange, onDelete, playing, isPaused, currentTimeSec }) {
  if (!col) return null;
  const title = resolveText(col.title);
  const body = resolveTextOptional(col.body);
  const bullets = col.bullets ? resolveTextArray(col.bullets) : null;
  const [titleBox, titleExit] = fieldAnims(col.title);
  const [bodyBox, bodyExit] = fieldAnims(col.body);
  const colBox = col.boxAnimation ?? null;
  const colExit = col.boxExitAnimation ?? null;

  return (
    <AnimatedField
      boxAnimation={colBox}
      boxExitAnimation={colExit}
      playing={playing}
      isPaused={isPaused}
      currentTimeSec={currentTimeSec}
    >
      <div
        style={{
          display: "grid",
          gap: "var(--space-3)",
          alignContent: "start",
          position: "relative",
        }}
      >
        {editing && onDelete && (
          <button
            onClick={onDelete}
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              background: "var(--color-bg-surface-strong)",
              border: "var(--stroke-thin) solid var(--color-border-subtle)",
              borderRadius: "var(--radius-sm)",
              color: "var(--color-text-muted)",
              cursor: "pointer",
              fontSize: "var(--text-xs)",
              padding: "2px 8px",
              lineHeight: 1.4,
            }}
            title="Delete column"
          >
            ✕
          </button>
        )}
        <AnimatedField
          boxAnimation={titleBox}
          boxExitAnimation={titleExit}
          playing={playing}
          isPaused={isPaused}
          currentTimeSec={currentTimeSec}
        >
          <Title size="lg">
            <EditableText
              value={title}
              editing={editing}
              onChange={(val) => onChange(`columns[${colIndex}].title`, val)}
            />
          </Title>
        </AnimatedField>
        {(body || editing) && (
          <AnimatedField
            boxAnimation={bodyBox}
            boxExitAnimation={bodyExit}
            playing={playing}
            isPaused={isPaused}
            currentTimeSec={currentTimeSec}
          >
            <Text role="body">
              <EditableText
                value={body || ""}
                editing={editing}
                onChange={(val) => onChange(`columns[${colIndex}].body`, val)}
              />
            </Text>
          </AnimatedField>
        )}
        {bullets && !editing && <BulletListUI items={bullets} />}
      </div>
    </AnimatedField>
  );
}

export function TwoColumnRenderer({
  element,
  editing,
  onChange,
  onReorder,
  playing,
  isPaused,
  currentTimeSec,
}) {
  const heading = element.heading ? resolveText(element.heading) : null;
  const eyebrow = element.eyebrow ? resolveText(element.eyebrow) : null;

  // Support element.columns array or fall back to element.left / element.right
  const columns = element.columns || [element.left, element.right].filter(Boolean);

  const handleDelete = (index) => {
    const next = columns.filter((_, i) => i !== index);
    onReorder("columns", next);
  };

  const handleAdd = () => {
    onReorder("columns", [...columns, { title: "New Column", body: "" }]);
  };

  const gridCols = columns.length > 0 ? `repeat(${columns.length}, 1fr)` : "1fr 1fr";

  return (
    <SurfaceCard compact>
      <div style={{ display: "grid", gap: "var(--space-6)" }}>
        {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
        {heading && <Title size="2xl">{heading}</Title>}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: gridCols,
            gap: "var(--space-6)",
            position: "relative",
          }}
        >
          {columns.map((col, i) => (
            <Column
              key={i}
              col={col}
              colIndex={i}
              editing={editing}
              onChange={onChange}
              onDelete={columns.length > 1 ? () => handleDelete(i) : null}
              playing={playing}
              isPaused={isPaused}
              currentTimeSec={currentTimeSec}
            />
          ))}
          {/* Divider lines between columns */}
          {columns.length > 1 &&
            Array.from({ length: columns.length - 1 }).map((_, i) => (
              <div
                key={`divider-${i}`}
                style={{
                  borderLeft: "var(--stroke-thin) solid var(--color-divider)",
                  position: "absolute",
                  left: `${((i + 1) / columns.length) * 100}%`,
                  top: 0,
                  bottom: 0,
                  pointerEvents: "none",
                }}
              />
            ))}
        </div>

        {editing && (
          <button
            onClick={handleAdd}
            style={{
              alignSelf: "start",
              justifySelf: "start",
              background: "none",
              border: "var(--stroke-thin) dashed var(--color-border-subtle)",
              borderRadius: "var(--radius-sm)",
              color: "var(--color-text-muted)",
              cursor: "pointer",
              fontSize: "var(--text-sm)",
              padding: "6px 14px",
            }}
          >
            + Add column
          </button>
        )}
      </div>
    </SurfaceCard>
  );
}

export default TwoColumnRenderer;
